/**
 * Multi-select of meeting-week Mondays (ISO YYYY-MM-DD). Same interaction +
 * styling as SectorMultiSelect — shared CSS module keeps the filter bar
 * visually consistent.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './SectorMultiSelect.module.css';

interface Props {
  /** ISO Mondays to offer. Shown in the order supplied. */
  dates: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}

function formatMeetingLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function shortRelative(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const days = Math.round((d.getTime() - now.getTime()) / (24 * 3600 * 1000));
  if (days === 0) return 'this week';
  if (days > 0 && days <= 7) return 'next week';
  if (days > 7) return `in ${Math.round(days / 7)}w`;
  if (days >= -7) return 'last week';
  return `${Math.round(-days / 7)}w ago`;
}

export function MeetingDateMultiSelect({ dates, selected, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return dates;
    return dates.filter((iso) => {
      const label = formatMeetingLabel(iso).toLowerCase();
      return iso.toLowerCase().includes(q) || label.includes(q);
    });
  }, [dates, search]);

  const toggle = (iso: string) => {
    const next = new Set(selected);
    if (next.has(iso)) next.delete(iso);
    else next.add(iso);
    onChange(next);
  };

  const clear = () => onChange(new Set());

  const selectVisible = () => {
    const next = new Set(selected);
    for (const d of filtered) next.add(d);
    onChange(next);
  };

  const triggerLabel =
    selected.size === 0
      ? 'Meetings: All'
      : selected.size === 1
        ? 'Meetings: 1 selected'
        : `Meetings: ${selected.size} selected`;

  return (
    <div ref={ref} className={styles.root}>
      <button
        type="button"
        className={`${styles.trigger} ${selected.size > 0 ? styles.triggerActive : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {triggerLabel}
        <span className={styles.chevron}>{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div className={styles.dropdown}>
          <div className={styles.searchRow}>
            <input
              ref={inputRef}
              type="search"
              className={styles.searchInput}
              placeholder="Filter meetings…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className={styles.actionsRow}>
            {search.trim() && filtered.length > 0 && (
              <button
                type="button"
                className={styles.actionBtn}
                onClick={selectVisible}
              >
                Select {filtered.length} visible
              </button>
            )}
            {selected.size > 0 && (
              <button
                type="button"
                className={styles.actionBtn}
                onClick={clear}
              >
                Clear ({selected.size})
              </button>
            )}
          </div>
          <div className={styles.options}>
            {dates.length === 0 ? (
              <div className={styles.noMatch}>
                No meetings have chunks attributed yet.
              </div>
            ) : filtered.length === 0 ? (
              <div className={styles.noMatch}>No meetings match "{search}"</div>
            ) : (
              filtered.map((iso) => (
                <label key={iso} className={styles.option}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={selected.has(iso)}
                    onChange={() => toggle(iso)}
                  />
                  <span className={styles.optionLabel}>{formatMeetingLabel(iso)}</span>
                  <span className={styles.optionStage}>{shortRelative(iso)}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
